import { DataAnnotationStatus } from '@robovision/data'
import type {
  HubAnnotationSource,
  HubCreateProjectDatapool,
  HubCreateSplit,
} from '@robovision/hub'
import { ImportExportRecordImportMode } from '@robovision/import-export'
import {
  addOrReplaceBy,
  distinct,
  HttpStatusCode,
  partition,
} from '@robovision/rvai-utils'
import JSZip from 'jszip'
import pLimit from 'p-limit'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import {
  CredibilityScore,
  CredibilityScoreThreshold,
} from '~domain/CredibilityScore'
import { Datapool } from '~domain/datapool/Datapool'
import { DatapoolMetadata } from '~domain/datapool/DatapoolMetadata'
import { PackageExtensions } from '~domain/Extensions'
import {
  ImportJob,
  ImportJobStatus,
} from '~domain/io/ImportJob'
import { i18n } from '~i18n'
import {
  dataApi,
  hubApi,
} from '~services/api'
import { acceptStatus } from '~utils/api'
import { getMaxSuffix } from '~utils/name'
import { extractBasename } from '~utils/path'
import { useImportJobsStore } from './useImportJobsStore'
import { useTagsStore } from './useTagsStore'


interface UploadData {
  files: File[]
  id: string
  importMode?: ImportExportRecordImportMode
  setEventSource?: boolean
}

// TODO move tags out of this store

export const useDatapoolsStore = defineStore('datapools', () => {
  const state = reactive({
    annotatedByUserCount: new Map<string, number>(),
    annotatedSamplesCount: new Map<string, number>(),
    datapools: [] as Datapool[],
    metadata: new Map<string, DatapoolMetadata>(),
    poolSources: new Map<string, string[]>(),
    tags: new Map<string, string[]>(),
    thumbnailUrls: new Map<string, string>(),
  })

  const getters = {
    getAnnotatedByUserCount (id: string): number {
      return state.annotatedByUserCount.get(id) ?? 0
    },

    async getDatapool (id: string): Promise<Datapool | undefined> {
      let datapool = state.datapools.find(d => d.id === id)
      if (!datapool) {
        datapool = await actions.loadDatapool(id)
      }
      return datapool
    },

    getDatapoolAnnotatedSamplesCount (id: string): number {
      return state.annotatedSamplesCount.get(id) ?? 0
    },

    getDatapoolSamplesCount (id: string): number | undefined {
      return state.metadata.get(id)?.sampleCount
    },

    getPoolSources (id: string): string[] | undefined {
      return state.poolSources.get(id)
    },
  }

  const actions = {
    clearTags (datapoolId: string) {
      state.tags.delete(datapoolId)
    },

    clearThumbnail (datapoolId: string) {
      state.thumbnailUrls.delete(datapoolId)
    },

    async createDatapool (data: HubCreateProjectDatapool): Promise<Datapool> {
      const { data: datapoolData } = await hubApi.datapools.createDatapool(data)

      const datapool = Datapool.fromResponse(datapoolData)
      state.datapools = [...state.datapools, datapool]

      return datapool
    },

    async deleteDatapoolData (id: string) {
      const {
        cancelImportJob,
        deleteImportJob,
        imports,
      } = useImportJobsStore()

      // delete all samples from the datapool
      await dataApi.pools.deleteSamples(id, { sampleIds: [] })

      const datapoolJobs = [...imports.values()]
        .filter(job => job.datapoolId === id)

      // delete all associated import jobs as well as their tags
      for (const job of datapoolJobs) {
        // cancel the ongoing import job; otherwise delete it
        const isUploading = job.status === ImportJobStatus.UPLOADING
        const isRunning = job.status === ImportJobStatus.STARTING || job.status === ImportJobStatus.RUNNING
        if (isRunning || isUploading) {
          await cancelImportJob(job.importId, true)
        } else {
          if (job.tagId) {
            await dataApi.tags.deleteTag(job.tagId)
          }
          state.tags.set(id, (state.tags.get(id) ?? []).filter(tag => tag !== job.name))
          await deleteImportJob(job.importId)
        }
      }

      // update datapool metadata
      await this.loadDatapoolMetadata(id)
      await this.loadDatapoolAnnotatedSamplesCount({
        id,
        minimumAnnotationCredibility: CredibilityScore.ModelAnnotation,
      })
    },

    async deleteDatapools (datapools: Datapool[]): Promise<void> {
      const limit = pLimit(10)
      const promises = datapools.map(datapool => limit(async () => {
        await hubApi.datapools.deleteDatapool(datapool.id)
        state.datapools = state.datapools.filter(d => d.id !== datapool.id)
      }))

      await Promise.all(promises)
    },

    async deleteSampleByImportJob (projectId: string, poolId: string, job: ImportJob) {
      const { deleteImportJob } = useImportJobsStore()
      const { deleteTags } = useTagsStore()

      if (!job.tagId) return

      await deleteTags({
        cascadeSamples: true,
        projectId,
        tagId: job.tagId,
      })
      state.tags.set(poolId, (state.tags.get(poolId) ?? []).filter(tag => tag !== job.name))

      await deleteImportJob(job.importId)
    },

    getDatapoolImportJobs (datapoolId?: string): ImportJob[] {
      if (!datapoolId) return []
      const { imports } = useImportJobsStore()

      return [...imports.values()]
        .filter(job => job.datapoolId === datapoolId)
    },

    getThumbnailUrl (datapoolId: string, forceLoad = false): string | undefined {
      if (!state.thumbnailUrls.has(datapoolId) || forceLoad) {
        if (!state.datapools.some(d => d.id === datapoolId)) {
          return undefined
        }

        // Append cache-busting param when refetching
        const queryString = state.thumbnailUrls.has(datapoolId) ? `?at=${Date.now()}` : ''
        const url = `/datastore/api/v1/pools/${datapoolId}/thumbnail${queryString}`
        state.thumbnailUrls.set(datapoolId, url)
        return url
      }
      return state.thumbnailUrls.get(datapoolId)
    },

    async loadAnnotatedSamplesCountBySource (id?: string, currentUserId?: string): Promise<number> {
      if (!id || !currentUserId) return 0

      const { data } = await dataApi.pools.countPoolSamples(id, { annotationSourceIds: [[currentUserId]] })

      state.annotatedByUserCount.set(id, data.count)
      return data.count
    },

    async loadDatapool (id: string): Promise<Datapool | undefined> {
      const response = await hubApi.datapools.getDatapool(
        id,
        {},
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )
      if (!response?.data) return undefined

      const { data } = response
      const datapool = Datapool.fromResponse(data)

      state.datapools = addOrReplaceBy(
        state.datapools,
        datapool,
        c => c.id === data.id,
      )

      return datapool
    },

    async loadDatapoolAnnotatedSamplesCount ({
      id,
      minimumAnnotationCredibility,
    }: {
      id?: string
      minimumAnnotationCredibility?: CredibilityScore
    }): Promise<number> {
      if (!id) return 0

      const { data } = await dataApi.pools.countPoolSamples(id, {
        annotationStatuses: [DataAnnotationStatus.COMPLETE, DataAnnotationStatus.PARTIAL],
        minimumAnnotationCredibility,
      })

      state.annotatedSamplesCount.set(id, data.count)
      return data.count
    },

    async loadDatapoolMetadata (id: string): Promise<void> {
      const datapool = state.datapools.find(d => d.id === id)
      if (!datapool) return

      const { data } = await dataApi.pools.getPoolMetadata(datapool.id)

      const metadata = DatapoolMetadata.fromResponse(data)

      state.metadata.set(id, metadata)
    },

    async loadDatapools (): Promise<void> {
      const { data } = await hubApi.datapools.listDatapools()

      const datapools = data.items.map(Datapool.fromResponse)
      state.datapools = datapools
    },

    async loadDatapoolSamplesCount (id?: string): Promise<number> {
      if (!id) return 0

      try {
        const { data } = await dataApi.pools.countPoolSamples(id)
        return data.count
      } catch (e) {
        console.error(e)
        return 0
      }
    },

    async loadDatapoolSources (datapoolId: string): Promise<HubAnnotationSource[]> {
      const { data: sources } = await hubApi.datapools.listDatapoolSources(
        datapoolId,
        {
          minimumAnnotationCredibility: CredibilityScoreThreshold.LabelCenter,
        },
      )

      return sources
    },

    async loadDatapoolsWithData (): Promise<void> {
      await this.loadDatapools()

      const limit = pLimit(10)
      const promises = state.datapools.map(d => limit(async () => {
        await this.loadDatapoolMetadata(d.id)
        await this.loadDatapoolAnnotatedSamplesCount({
          id: d.id,
          minimumAnnotationCredibility: CredibilityScore.ModelAnnotation,
        })
      }))
      await Promise.all(promises)
    },

    async loadPoolSources (
      id: string,
      minimumAnnotationCredibility: CredibilityScoreThreshold = CredibilityScoreThreshold.DatasetCreation,
    ): Promise<string[]> {
      const { data } = await dataApi.pools.listPoolSources(id, {
        minimumAnnotationCredibility,
      })

      state.poolSources.set(id, data.sourceIds)

      return data.sourceIds
    },

    setDatapoolTags (id: string, tags: string[]): void {
      state.tags.set(id, tags)
    },

    setPoolSources (id: string, sources: string[]): void {
      state.poolSources.set(id, sources)
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async splitDatapool (id: string, data: HubCreateSplit) {
      const { data: splits } = await hubApi.datapools.createSplit(id, data)
      return splits
    },

    async uploadData ({
      files,
      id,
      importMode = ImportExportRecordImportMode.ANY,
      setEventSource = false,
    }: UploadData): Promise<string[]> {
      const { startImportJob } = useImportJobsStore()
      const datapool = state.datapools.find(d => d.id === id)
      if (!datapool) return []

      const importIds: string[] = []

      // splits the file by whether or not its extension is in the list
      // of package extensions supported by the importer service (e.g. .zip).
      // we will send the package files as is but will bundle the remaining
      // files on a separated zip.
      const [
        packages,
        loose,
      ] = partition(
        files,
        f => PackageExtensions.some(ext => f.name.toLowerCase().endsWith(ext)),
      )

      const { data } = await dataApi.pools.listPoolTags(id, { limit: null })
      state.tags.set(id, distinct([...(state.tags.get(id) ?? []), ...data.tags.map(t => t.name)]))

      for (const file of packages) {
        let name = extractBasename(file.name)
        const suffix = getMaxSuffix(name, state.tags.get(id) ?? [])
        if (name && suffix != null) name = `${name} ${suffix + 1}`

        // add the new name to the list of existing names to
        // avoid conflicts when 2 selected files only differ from
        // each other in the extension.
        state.tags.set(id, [...(state.tags.get(id) ?? []), name])

        const importId = startImportJob({
          datapoolId: datapool.id,
          file,
          importMode,
          setEventSource,
          tag: name,
        })
        importIds.push(importId)
      }

      if (loose.length) {
        const zip = new JSZip()
        for (const file of loose) {
          zip.file(file.name, file)
        }
        const zipped = zip.generateAsync({ type: 'blob' })
          .then(blob => new File([blob], `${uuidv4()}.zip`))

        const baseName = i18n.t('projects.importJobs.defaultImportName')
        const suffix = getMaxSuffix(baseName, state.tags.get(id) ?? []) ?? 0
        const name = `${baseName}-${suffix + 1}`

        state.tags.set(id, [...(state.tags.get(id) ?? []), name])

        const importId = startImportJob({
          datapoolId: datapool.id,
          file: zipped,
          importMode,
          setEventSource,
          tag: name,
        })
        importIds.push(importId)
      }

      return importIds
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...getters,
    ...actions,
  }
})
